import React from 'react';
import { createRoot } from 'react-dom/client';

import { SchoolReviewForm } from '../main-app/components/ReviewForm';
import stores from '../main-app/stores';

// Self-mounting entry pack for the school review form.
//
// Previously this was a lazy React child of schoolPageEntry's Suspense that
// called createRoot(node) at the top of its render body (before the null guard,
// so it could call createRoot(null) and throw) and called hooks around an early
// return (Rules-of-Hooks violation). Suspense re-rendering re-ran createRoot on
// the same node and produced the duplicate-createRoot warning.
//
// It now mounts itself at DOMContentLoaded, creating exactly ONE root on
// #react-school-review-form, and preserves the original deferred-render
// behavior imperatively (the component already owned its root imperatively):
//   - the form only renders once it becomes visible (isFormVisible), which is
//     triggered by a click on any .vv-post-rating-btn or when the URL hash is
//     '#post-review';
//   - when the node initially has no data-school-id, a MutationObserver waits
//     for it to appear, then unmounts, resets the relevant MobX stores, and
//     re-renders with the new ids.
document.addEventListener('DOMContentLoaded', () => {
  const node = document.getElementById('react-school-review-form');

  if (!node) return;

  const root = createRoot(node);
  let isFormVisible = window.location.hash === '#post-review';

  const renderForm = ({
    schoolId, placeId, userSchools, minCommentLength,
  }) => {
    if (!isFormVisible) return;
    root.render(
      <SchoolReviewForm
        minCommentLength={minCommentLength}
        schoolId={schoolId}
        userSchools={userSchools}
        placeId={placeId}
      />,
    );
  };

  const mountFromNode = () => {
    let schoolId = node.getAttribute('data-school-id');
    let placeId = node.getAttribute('data-place-id');
    let minCommentLength = node.getAttribute('data-school-min-comment-length');

    if (schoolId) {
      renderForm({ schoolId, placeId, minCommentLength });
      return;
    }

    const userSchools = JSON.parse(node.getAttribute('data-user-schools'));
    if (userSchools) {
      schoolId = userSchools[0].id;
      placeId = userSchools[0].placeId;
      minCommentLength = userSchools[0].min_comment_length;
      renderForm({
        schoolId, userSchools, placeId, minCommentLength,
      });
      return;
    }

    // No ids yet: wait for the node to receive a data-school-id, then reset
    // stores and re-render (school switch on the ratings admin view).
    const observer = new MutationObserver((mutationsList) => {
      mutationsList.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-school-id') {
          root.unmount();
          stores.schoolReviewStore.isSent = false;
          stores.meetingStore.meetingSubjects = [];

          const nextSchoolId = node.getAttribute('data-school-id');
          const nextPlaceId = node.getAttribute('data-place-id');
          const nextMinCommentLength = node.getAttribute('data-school-min-comment-length');
          renderForm({
            schoolId: nextSchoolId,
            placeId: nextPlaceId,
            minCommentLength: nextMinCommentLength,
          });
        }
      });
    });
    observer.observe(node, { attributes: true });
  };

  const showForm = () => {
    if (isFormVisible) return;
    isFormVisible = true;
    mountFromNode();
  };

  // Reveal the form when the user clicks a "post a review" button.
  const bottomReviewButtons = document.getElementsByClassName('vv-post-rating-btn');
  Array.from(bottomReviewButtons).forEach((button) => {
    button.addEventListener('click', showForm);
  });

  // Reveal immediately when deep-linked via the #post-review hash.
  if (isFormVisible) {
    mountFromNode();
  }
});
